import { useEffect, useMemo, useState } from "react";
import { Badge, Button, Center, Flex, Loader, Text, Title, Tooltip, ActionIcon, useMantineTheme } from "@mantine/core";
import { MantineReactTable, type MRT_ColumnDef } from "mantine-react-table";
import { IconEye, IconPlus } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";

type NotificationItem = {
  id: string;
  title: string;
  body: string;
  recipientUserIds: string[] | null;
  scheduledAt: Date | null;
  sentAt: Date | null;
  createdAt: Date;
};

function formatNotifDate(date: Date | string | null | undefined): string {
  if (!date) return "—";
  return new Date(date as string).toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatNotifTime(date: Date | string | null | undefined): string {
  if (!date) return "—";
  return new Date(date as string).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const theme = useMantineTheme();

  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNotifications = async () => {
      setLoading(true);
      try {
        const res = await trpc.appUserNotification.list.query();
        setNotifications(res as NotificationItem[]);
      } catch (err) {
        handleTrpcError(err);
        setNotifications([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, []);

  const columns: MRT_ColumnDef<NotificationItem>[] = useMemo(
    () => [
      {
        accessorKey: "sentAt",
        header: "Date",
        size: 180,
        filterVariant: "date-range",
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.sentAt ?? rowA.original.scheduledAt ?? rowA.original.createdAt;
          const b = rowB.original.sentAt ?? rowB.original.scheduledAt ?? rowB.original.createdAt;
          return new Date(a).getTime() - new Date(b).getTime();
        },
        filterFn: (row, _id, value: [Date | null, Date | null]) => {
          const date = new Date(row.original.sentAt ?? row.original.scheduledAt ?? row.original.createdAt);
          const [min, max] = value;
          if (min && date < new Date(min)) return false;
          if (max && date > new Date(max)) return false;
          return true;
        },
        mantineFilterTextInputProps: { placeholder: "Filter by date" },
        Cell: ({ row }) => {
          const date = row.original.sentAt ?? row.original.scheduledAt ?? row.original.createdAt;
          return formatNotifDate(date);
        },
      },
      {
        id: "time",
        header: "Time",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          const date = row.original.sentAt ?? row.original.scheduledAt ?? row.original.createdAt;
          return formatNotifTime(date);
        },
      },
      {
        id: "recipient",
        header: "Recipient",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          const isAll = !row.original.recipientUserIds || row.original.recipientUserIds.length === 0;
          return (
            <Badge variant="light" color={isAll ? "violet" : "blue"} size="sm">
              {isAll ? "ALL" : "CUSTOM"}
            </Badge>
          );
        },
      },
      {
        accessorKey: "title",
        header: "Title",
        size: 200,
        enableColumnFilter: false,
        enableSorting: true,
        Cell: ({ cell }) => (
          <Text fz="sm" truncate>
            {cell.getValue<string>()}
          </Text>
        ),
      },
      {
        accessorKey: "body",
        header: "Content",
        size: 220,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => (
          <Text fz="sm" truncate>
            {cell.getValue<string>()}
          </Text>
        ),
      },
    ],
    []
  );

  if (loading) {
    return (
      <Center h="100vh">
        <Loader size="lg" type="dots" />
      </Center>
    );
  }

  return (
    <Flex direction="column" w="100%" p="xl">
      <Flex justify="space-between" align="center" mb="xl">
        <Title order={2} fw={700}>
          Notifications
        </Title>
        <Button leftSection={<IconPlus size={16} />} color="primary" onClick={() => navigate("/notifications/create")}>
          Create notification
        </Button>
      </Flex>

      <MantineReactTable
        columns={columns}
        data={notifications}
        enableRowNumbers
        enableStickyHeader
        enableColumnPinning
        enableRowActions
        enableColumnResizing
        columnResizeMode="onEnd"
        layoutMode="semantic"
        enableGlobalFilter={false}
        positionToolbarAlertBanner="none"
        positionActionsColumn="last"
        paginationDisplayMode="pages"
        mantinePaginationProps={{
          rowsPerPageOptions: ["10", "25", "50", "100"],
        }}
        initialState={{
          columnPinning: { right: ["mrt-row-actions"] },
          density: "xs",
          pagination: { pageIndex: 0, pageSize: 15 },
          sorting: [{ id: "sentAt", desc: true }],
        }}
        renderRowActions={({ row }) => (
          <Tooltip label="View Details">
            <ActionIcon c="slate.6" variant="transparent" onClick={() => navigate(`/notifications/${row.original.id}`)}>
              <IconEye size={18} />
            </ActionIcon>
          </Tooltip>
        )}
        mantineTableBodyCellProps={{
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            borderRight: "1px solid var(--mantine-color-gray-2)",
          },
        }}
        mantineTableHeadCellProps={{
          style: {
            fontWeight: 400,
            fontSize: "var(--mantine-font-size-sm)",
            color: theme.colors.slate[5],
            borderRight: "1px solid var(--mantine-color-gray-2)",
            backgroundColor: "var(--mantine-color-gray-0)",
          },
        }}
        mantineTableContainerProps={{
          style: {
            maxHeight: "calc(100vh - 220px)",
            minHeight: "100px",
            width: "100%",
            overflowX: "auto",
          },
        }}
        renderEmptyRowsFallback={() => (
          <tr>
            <td colSpan={columns.length + 2} style={{ textAlign: "center", padding: "2rem" }}>
              No notifications found.
            </td>
          </tr>
        )}
      />
    </Flex>
  );
}
